import React, { useState, useEffect } from 'react';
import MetricCard from './MetricCard';
import ChartCard from './ChartCard';
import ActivityFeed from './ActivityFeed';
import { apiService } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';

interface VendorDashboardProps {
  className?: string;
}

const VendorDashboard: React.FC<VendorDashboardProps> = ({ className = '' }) => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<any>({});
  const [qrUsageData, setQrUsageData] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    loadDashboardData();
  }, [period]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load metrics
      const metricsResponse = await apiService.dashboard.getMetrics(period);
      setMetrics(metricsResponse.data.data);

      // Load chart data
      const [qrResponse, activityResponse] = await Promise.all([
        apiService.dashboard.getChartData('qr-usage', period),
        apiService.dashboard.getRecentActivity(6)
      ]);

      setQrUsageData(qrResponse.data.data?.data || []);
      setActivities(activityResponse.data.data?.activities || []);
    } catch (error) {
      console.error('Error loading vendor dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSubscriptionStatusColor = (status: any) => {
    if (!status?.hasActiveSubscription) return 'text-meta-1';
    
    const daysUntilRenewal = status.daysUntilRenewal || 0;
    if (daysUntilRenewal <= 7) return 'text-meta-1';
    if (daysUntilRenewal <= 30) return 'text-meta-7';
    return 'text-meta-3';
  };

  const getSubscriptionStatusText = (status: any) => {
    if (!status?.hasActiveSubscription) return 'No Active Subscription';
    
    const daysUntilRenewal = status.daysUntilRenewal || 0;
    if (daysUntilRenewal <= 0) return 'Expired';
    if (daysUntilRenewal <= 7) return `Expires in ${daysUntilRenewal} days`;
    if (daysUntilRenewal <= 30) return `Renews in ${daysUntilRenewal} days`;
    return 'Active';
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-black dark:text-white">
            Business Dashboard
          </h2>
          <p className="text-meta-6">
            Welcome back! Here's your business overview
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="rounded border border-stroke bg-transparent py-2 px-3 text-black dark:text-white dark:border-form-strokedark dark:bg-form-input focus:border-primary focus:outline-none"
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      {/* Subscription Status Alert */}
      {metrics.subscriptionStatus && !metrics.subscriptionStatus.hasActiveSubscription && (
        <div className="rounded-sm border border-meta-1 bg-meta-1/10 p-4">
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-meta-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5Z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-meta-1">No Active Subscription</p>
              <p className="text-xs text-meta-6">Contact support to renew your subscription and continue using our services.</p>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Scans"
          value={metrics.totalScans || 0}
          loading={loading}
          icon={
            <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7Z" />
            </svg>
          }
        />

        <MetricCard
          title="Active QR Codes"
          value={metrics.qrCodeCount || 0}
          loading={loading}
          icon={
            <svg className="w-6 h-6 text-meta-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1Zm12 0h2a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1ZM5 20h2a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1Z" />
            </svg>
          }
        />

        <MetricCard
          title="Menu Items"
          value={metrics.menuItemsCount || 0}
          loading={loading}
          icon={
            <svg className="w-6 h-6 text-meta-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2m0-8V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2m0 0V9a2 2 0 0 0-2-2H9m0 0h6" />
            </svg>
          }
        />

        <MetricCard
          title="QR Utilization"
          value={metrics.qrUtilization || 0}
          valueSuffix="%"
          loading={loading}
          icon={
            <svg className="w-6 h-6 text-meta-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2Zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v10Z" />
            </svg>
          }
        />
      </div>

      {/* QR Usage Chart & Subscription Status */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ChartCard
            title="QR Code Scans Over Time"
            data={qrUsageData}
            type="line"
            loading={loading}
            xDataKey="date"
            yDataKey="scans"
            color="#06B6D4"
            dateFormat="daily"
            valueType="number"
            formatTooltip={(value, name) => [`${value} scans`, 'Daily Scans']}
          />
        </div>

        {/* Subscription Status */}
        <div className="rounded-sm border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
          <h4 className="text-xl font-semibold text-black dark:text-white mb-4">
            Subscription Status
          </h4>
          <div className="space-y-4">
            {metrics.subscriptionStatus?.hasActiveSubscription ? (
              <>
                <div className="text-center p-4 bg-meta-3/10 rounded-lg">
                  <div className="w-12 h-12 bg-meta-3 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-black dark:text-white">
                    {metrics.subscriptionStatus.pricingName || 'Active Plan'}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-meta-6">Status</span>
                    <span className={`text-sm font-medium ${getSubscriptionStatusColor(metrics.subscriptionStatus)}`}>
                      {getSubscriptionStatusText(metrics.subscriptionStatus)}
                    </span>
                  </div>
                  
                  {metrics.subscriptionStatus.renewalDate && (
                    <div className="flex justify-between">
                      <span className="text-sm text-meta-6">Renewal Date</span>
                      <span className="text-sm font-medium text-black dark:text-white">
                        {new Date(metrics.subscriptionStatus.renewalDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <span className="text-sm text-meta-6">Active Subscriptions</span>
                    <span className="text-sm font-medium text-black dark:text-white">
                      {metrics.activeSubscriptions || 0}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center p-4 bg-meta-1/10 rounded-lg">
                <div className="w-12 h-12 bg-meta-1 rounded-full flex items-center justify-center mx-auto mb-2">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-meta-1 mb-2">
                  No Active Subscription
                </p>
                <p className="text-xs text-meta-6">
                  Contact support to activate your subscription
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Quick Actions */}
        <div className="rounded-sm border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
          <h4 className="text-xl font-semibold text-black dark:text-white mb-4">
            Quick Actions
          </h4>
          <div className="space-y-3">
            <Link
              to="#"
              className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2m0-8V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2m0 0V9a2 2 0 0 0-2-2H9m0 0h6" />
                </svg>
              </div>
              <span className="text-sm font-medium text-black dark:text-white">
                Update Menu
              </span>
            </Link>

            <Link
              to="#"
              className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="w-8 h-8 bg-meta-3 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1Zm12 0h2a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1ZM5 20h2a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1Z" />
                </svg>
              </div>
              <span className="text-sm font-medium text-black dark:text-white">
                View QR Codes
              </span>
            </Link>

            <Link
              to="/admin/subscriptions"
              className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="w-8 h-8 bg-meta-5 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 0 0 3-3V8a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3Z" />
                </svg>
              </div>
              <span className="text-sm font-medium text-black dark:text-white">
                Manage Subscription
              </span>
            </Link>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <ActivityFeed
            activities={activities}
            loading={loading}
            title="Recent Activity"
            maxItems={5}
          />
        </div>
      </div>
    </div>
  );
};

export default VendorDashboard;